import classNames from "classnames";
import find from "lodash/find";
import React, { FunctionComponent, ReactNode } from "react";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { Template } from "../../../../types/customization/template";
import { imageUrlFromAssetDefinition } from "../../../../utils/customization/asset";
import { lengthMmToPx } from "../../../../utils/customization/box";
import { firstMatchingCondition } from "../../../../utils/customization/condition";
import {
  findAssetDefinition,
  findPageDefinition,
  findPresentationDefinition,
} from "../../../../utils/customization/finders";
import ClippingPath from "../ClippingPath";
import "./style.scss";

interface Props {
  children: ReactNode;
  pageDefinitionKey: string;
  ppmm: number;
  template: Template;
}

const PagePresentation: FunctionComponent<Props> = ({
  children,
  pageDefinitionKey,
  ppmm,
  template,
}) => {
  const { configuration } = useCustomizationContext();
  const pageDefinition = findPageDefinition(pageDefinitionKey, template);
  const pageHeight = lengthMmToPx(pageDefinition.height, ppmm).value;
  const pageWidth = lengthMmToPx(pageDefinition.width, ppmm).value;

  const shapeDefinition = find(
    template.shapeDefinitions,
    ({ key }) => key === pageDefinition.shapeKey
  );
  const clippingSVG = shapeDefinition ? shapeDefinition.svgContent : null;
  const clippingId = shapeDefinition ? `clipping-${pageDefinitionKey}` : null;

  const presentation = firstMatchingCondition(
    pageDefinition.presentations,
    template,
    configuration
  );
  if (!presentation) {
    const wrapperBlankStyle = {
      height: pageHeight,
      width: pageWidth,
    };

    const pageBlankStyle = {
      left: 0,
      top: 0,

      height: pageHeight,
      width: pageWidth,
    };

    return (
      <>
        {clippingSVG && clippingId && (
          <PagePresentationClipPath
            contentSVG={clippingSVG}
            id={clippingId}
            wrapperStyle={wrapperBlankStyle}
          />
        )}
        <div
          className={classNames("PagePresentation", {
            "PagePresentation--clipped": clippingSVG,
          })}
          style={{
            ...wrapperBlankStyle,
            WebkitClipPath: clippingId ? `url(#${clippingId})` : "",
            clipPath: clippingId ? `url(#${clippingId})` : "",
          }}
        >
          <div className="PagePresentation-page" style={pageBlankStyle}>
            {children}
          </div>
        </div>
      </>
    );
  }

  const presentationDefinition = findPresentationDefinition(
    presentation.presentationDefinitionKey,
    template
  );
  const assetDefinition = findAssetDefinition(presentationDefinition.assetKey, template);
  const assetWidth = Math.ceil(lengthMmToPx(presentationDefinition.width, ppmm).value);
  const assetUrlTemplate = configuration && configuration.assetUrlTemplate;
  const imageUrl = imageUrlFromAssetDefinition(assetDefinition, assetWidth, assetUrlTemplate);

  const wrapperStyle = {
    height: Math.floor(lengthMmToPx(presentationDefinition.height, ppmm).value),
    width: Math.floor(lengthMmToPx(presentationDefinition.width, ppmm).value),
  };

  const pageStyle = {
    left: Math.round(lengthMmToPx(presentationDefinition.contentX, ppmm).value),
    top: Math.round(lengthMmToPx(presentationDefinition.contentY, ppmm).value),

    height: pageHeight,
    width: pageWidth,
  };

  let abovePresentationClass = "";
  if (presentationDefinition.above === true) {
    abovePresentationClass = "PagePresentation-asset--above";
  } else {
    wrapperStyle.height = wrapperStyle.height - 2;
    wrapperStyle.width = wrapperStyle.width - 2;
    pageStyle.left = pageStyle.left - 1;
    pageStyle.top = pageStyle.top - 1;
  }

  return (
    <>
      {clippingSVG && clippingId && (
        <PagePresentationClipPath
          contentSVG={clippingSVG}
          id={clippingId}
          wrapperStyle={wrapperStyle}
        />
      )}
      <div
        className={classNames("PagePresentation", {
          "PagePresentation--clipped": clippingSVG,
        })}
        style={{
          ...wrapperStyle,
          WebkitClipPath: clippingId ? `url(#${clippingId})` : "",
          clipPath: clippingId ? `url(#${clippingId})` : "",
        }}
      >
        <img
          alt=""
          className={classNames("PagePresentation-asset", abovePresentationClass)}
          src={imageUrl}
          style={wrapperStyle}
        />

        <div className="PagePresentation-page" style={pageStyle}>
          {children}
        </div>
      </div>
    </>
  );
};

const PagePresentationClipPath: FunctionComponent<{
  contentSVG: string;
  id: string;
  wrapperStyle: {
    height: number;
    width: number;
  };
}> = ({ contentSVG, id, wrapperStyle }) => {
  return (
    <div className="PagePresentationClipPath-shadow" style={{ ...wrapperStyle }}>
      <div
        className="PagePresentationClipPath-shadow-content"
        style={{
          WebkitClipPath: `url(#${id})`,
          clipPath: `url(#${id})`,
        }}
      >
        <ClippingPath contentSVG={contentSVG} id={id} />
      </div>
    </div>
  );
};

export default PagePresentation;
